import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CircleCheck, Mail, RotateCcw, Search, Send, ShieldAlert } from 'lucide-react';
import type { AdminSupportTicket, AdminSupportTicketDetail, SupportTicketStatus } from '@spawnwire/shared';
import { Button, Input, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { formatDate } from '../../lib/moderation';
import { BackLink, Conversation, ReplyBox, StatusPill, TicketHeader, ticketTopic, type StatusLabels } from '../support/TicketParts';
import { Card, Empty, PageHeader, Tabs, UserChip } from './adminUi';
import { loadAdminStats } from './store';

/** How a ticket's status reads to staff. */
const STAFF_STATUS: StatusLabels = {
  open: { label: 'Waiting for staff', dot: 'bg-warning' },
  answered: { label: 'Answered', dot: 'bg-accent' },
  closed: { label: 'Closed', dot: 'bg-faint' },
};

export function SupportPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SupportTicketStatus | 'all'>('open');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<AdminSupportTicket[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (offset = 0) => {
      try {
        const page = await http.get<AdminSupportTicket[]>(`/admin/tickets?status=${status}&q=${encodeURIComponent(q)}&offset=${offset}`);
        setRows((prev) => (offset && prev ? [...prev, ...page] : page));
        setMore(page.length === 50);
        setError(null);
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [status, q],
  );

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  function search(e: FormEvent) {
    e.preventDefault();
    setQ(query.trim());
  }

  return (
    <div>
      <PageHeader title="Support" description="Tickets from people who need help, signed in or not. Waiting tickets show the longest-waiting first.">
        <Tabs
          value={status}
          onChange={setStatus}
          options={[
            { value: 'open', label: 'Waiting' },
            { value: 'answered', label: 'Answered' },
            { value: 'closed', label: 'Closed' },
            { value: 'all', label: 'All' },
          ]}
        />
      </PageHeader>
      <form onSubmit={search} className="mb-4 flex gap-2">
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ticket number, subject, email or username" aria-label="Search tickets" />
        <Button type="submit" variant="secondary" icon={Search}>
          Search
        </Button>
      </form>
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>{q ? 'No tickets match that search.' : status === 'open' ? 'Nobody is waiting for a reply. Nice.' : 'Nothing here.'}</Empty>
      ) : (
        <ul className="space-y-2">
          {rows.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => navigate(`/app/admin/support/${t.id}`)}
                className="w-full rounded-xl border border-line bg-sidebar p-4 text-left transition-colors hover:bg-elevated"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-faint">#{t.number}</span>
                  <span className="min-w-0 flex-1 truncate font-semibold text-fg">{t.subject}</span>
                  <StatusPill status={t.status} labels={STAFF_STATUS} />
                </span>
                <span className="mt-1 block truncate text-sm text-muted">{t.preview}</span>
                <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-faint">
                  <span>{ticketTopic(t)}</span>
                  <span>{t.signedIn && t.account ? `@${t.account.username}` : t.email}</span>
                  {!t.signedIn && <span className="font-medium text-warning">Signed out</span>}
                  <span>
                    {t.messageCount} message{t.messageCount === 1 ? '' : 's'}
                  </span>
                  <span className="ml-auto">Updated {formatDate(t.updatedAt)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {more && rows && (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => void load(rows.length)}>
          Load more
        </Button>
      )}
    </div>
  );
}

/** Who opened the ticket, and how much to trust that. */
function Requester({ ticket }: { ticket: AdminSupportTicketDetail }) {
  return (
    <Card>
      <p className="text-xs font-semibold uppercase tracking-wide text-faint">From</p>
      <div className="mt-2 space-y-3 text-sm">
        {ticket.signedIn ? (
          <UserChip user={ticket.account} size={32} />
        ) : (
          <div className="flex items-start gap-2 rounded-lg bg-warning/10 p-3 text-muted">
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <p>
              <span className="font-semibold text-fg">Sent while signed out.</span> Anyone can type an email address, so this may not be the account owner. Don't share account
              details or change the account until they prove it's theirs.
            </p>
          </div>
        )}
        <p className="flex items-center gap-2 break-all text-fg">
          <Mail className="size-4 shrink-0 text-muted" aria-hidden /> {ticket.email}
        </p>
        {!ticket.signedIn &&
          (ticket.account ? (
            <div>
              <p className="mb-1 text-xs text-muted">Account using this email</p>
              <UserChip user={ticket.account} size={28} />
            </div>
          ) : (
            <p className="text-xs text-muted">No account uses this email.</p>
          ))}
        {ticket.server && (
          <p className="text-muted">
            Game server: <span className="font-medium text-fg">{ticket.server.name}</span>
            {!ticket.server.id && ' (deleted)'}
          </p>
        )}
        <p className="text-xs text-faint">Opened {formatDate(ticket.createdAt)}</p>
      </div>
    </Card>
  );
}

export function TicketPage() {
  const { ticketId = '' } = useParams();
  const [ticket, setTicket] = useState<AdminSupportTicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTicket(null);
    http
      .get<AdminSupportTicketDetail>(`/admin/tickets/${ticketId}`)
      .then(setTicket)
      .catch((err) => setError(errorMessage(err)));
  }, [ticketId]);

  async function changed(next: Promise<AdminSupportTicketDetail>) {
    setTicket(await next);
    void loadAdminStats();
  }

  async function setStatus(status: 'open' | 'closed') {
    setBusy(true);
    try {
      await changed(http.post<AdminSupportTicketDetail>(`/admin/tickets/${ticketId}/status`, { status }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!ticket) return error ? <Empty>{error}</Empty> : <Spinner />;
  const closed = ticket.status === 'closed';
  return (
    <div className="space-y-5">
      <BackLink to="/app/admin/support">Support</BackLink>
      <TicketHeader ticket={ticket} labels={STAFF_STATUS} />
      {error && <p className="text-sm text-danger">{error}</p>}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="min-w-0 space-y-5">
          <Conversation
            messages={ticket.messages}
            who={(m) =>
              m.fromStaff ? (
                <span className="flex items-center gap-1.5">
                  {m.author ? m.author.displayName : 'Staff'}
                  <span className="rounded bg-accent px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-accent-fg">Staff</span>
                </span>
              ) : (
                (m.author?.displayName ?? ticket.email)
              )
            }
          />
          <ReplyBox
            label="Reply"
            placeholder="They see it signed as Spawnwire Support, not with your name."
            note={ticket.signedIn ? 'They also get a DM from Spawnwire saying you replied. Ctrl+Enter sends.' : "Sent while signed out: they only see replies on the ticket's private page. Ctrl+Enter sends."}
            actions={[
              { label: 'Send reply', icon: Send, sends: true, run: (message) => changed(http.post(`/admin/tickets/${ticketId}/messages`, { message })) },
              { label: 'Reply and close', icon: CircleCheck, sends: true, run: (message) => changed(http.post(`/admin/tickets/${ticketId}/messages`, { message, close: true })) },
            ]}
          />
        </div>
        <aside className="space-y-3">
          <Requester ticket={ticket} />
          <Button className="w-full" variant="secondary" icon={closed ? RotateCcw : CircleCheck} loading={busy} onClick={() => void setStatus(closed ? 'open' : 'closed')}>
            {closed ? 'Reopen ticket' : 'Close without replying'}
          </Button>
        </aside>
      </div>
    </div>
  );
}
